import { Injectable } from '@angular/core';

import { AppDomService } from './app-dom.service';
import { IIdWithValue, IRevereCheckoutDialogConfig, IRevereCheckoutDialogResult } from './interfaces';
import { ClassName } from './enums';

@Injectable({
    providedIn: 'root'
})
export class RevereChechkoutModalDialogService {
    private selectors = {
        dialog: '[aza-rws-revere-checkout-dialog]',
        modalDialogOverlay: '[aza-rws-modal-dialog-overlay]',
        cancelButton: '[aza-rws-modal-dialog-first-button]',
        proceedButton: '[aza-rws-modal-dialog-second-button]',
        ccNumberFieldValidationError: '[aza-rws-revere-checkout-ccnumber-field-validation-error]',
        ccExpirationFieldValidationError: '[aza-rws-revere-checkout-ccexp-field-validation-error]',
        cvvFieldValidationError: '[aza-rws-revere-checkout-cvv-field-validation-error]',
        azaRwsRevereCheckoutTotalValue: '[aza-rws-revere-checkout-total-value]',
        azaRwsRevereCheckoutCurrencyValue: '[aza-rws-revere-checkout-currency-value]',
        azaRwsRevereCheckoutFirstNameField: '[aza-rws-revere-checkout-first_name-field]',
        azaRwsRevereCheckoutLastNameField: '[aza-rws-revere-checkout-last_name-field]',
        azaRwsRevereCheckoutEmailField: '[aza-rws-revere-checkout-email-field]',
    };
    private modalDialogEl: HTMLElement;
    private cancelBtnEl: HTMLButtonElement;
    private proceedBtnEl: HTMLButtonElement;
    private flags: IFlags = {};

    constructor(private readonly domSvc: AppDomService) {
    }

    showDialog(config: IRevereCheckoutDialogConfig): Promise<IRevereCheckoutDialogResult> {
        this.flags = {};
        this.modalDialogEl ||= document.querySelector(this.selectors.dialog);
        this.cancelBtnEl ||= this.modalDialogEl.querySelector(this.selectors.cancelButton) as HTMLButtonElement;
        this.proceedBtnEl ||= this.modalDialogEl.querySelector(this.selectors.proceedButton) as HTMLButtonElement;
        const modalDialogOverlayEl = document.querySelector(this.selectors.modalDialogOverlay);
        this.domSvc.showElementWithCss(this.modalDialogEl);
        this.domSvc.showElementWithCss(modalDialogOverlayEl);
        const currentOrderTotalEl: HTMLElement = this.modalDialogEl.querySelector(this.selectors.azaRwsRevereCheckoutTotalValue);
        currentOrderTotalEl.innerText = `${config.currentOrderTotal || 0}`;
        const currentOrderCurrencyEl: HTMLElement = this.modalDialogEl.querySelector(this.selectors.azaRwsRevereCheckoutCurrencyValue);
        currentOrderCurrencyEl.innerText = `${config.cartCurrency || ''}`;
        return new Promise<IRevereCheckoutDialogResult>((resolve, reject) => {
            this.cancelBtnEl.onclick = () => resolve({ proceed: false });
            this.proceedBtnEl.onclick = () => resolve(this.getDialogResult(true));
        });
    }

    hideDialog(): void {
        const modalDialogOverlayEl = document.querySelector(this.selectors.modalDialogOverlay);
        const ccNumberValidationErrorEl = this.modalDialogEl.querySelector(this.selectors.ccNumberFieldValidationError) as HTMLDivElement;
        const ccExpirationValidationErrorEl = this.modalDialogEl.querySelector(this.selectors.ccExpirationFieldValidationError) as HTMLDivElement;
        const cvvValidationErrorEl = this.modalDialogEl.querySelector(this.selectors.cvvFieldValidationError) as HTMLDivElement;
        this.domSvc.hideElementWithCss(this.modalDialogEl);
        this.domSvc.hideElementWithCss(modalDialogOverlayEl);
        this.domSvc.hideElementWithCss(ccNumberValidationErrorEl);
        this.domSvc.hideElementWithCss(ccExpirationValidationErrorEl);
        this.domSvc.hideElementWithCss(cvvValidationErrorEl);
    }

    processInvalidFields(invalidFields: IIdWithValue[]): void {
        // Revere is calling this callback even after we start processing the values
        // If this happens, we already disabled the process button but this is called after that and we will enable it again
        if (this.flags.paymentStarted) {
            return;
        }

        const hasInvalidFields = invalidFields.length > 0;
        if (hasInvalidFields) {
            this.disableButton(this.proceedBtnEl);
        } else {
            this.enableButton(this.proceedBtnEl);
        }
        const ccNumberValidationErrorEl = this.modalDialogEl.querySelector(this.selectors.ccNumberFieldValidationError) as HTMLDivElement;
        const ccExpirationValidationErrorEl = this.modalDialogEl.querySelector(this.selectors.ccExpirationFieldValidationError) as HTMLDivElement;
        const cvvValidationErrorEl = this.modalDialogEl.querySelector(this.selectors.cvvFieldValidationError) as HTMLDivElement;
        this.domSvc.hideElementWithCss(ccNumberValidationErrorEl);
        this.domSvc.hideElementWithCss(ccExpirationValidationErrorEl);
        this.domSvc.hideElementWithCss(cvvValidationErrorEl);

        for (const invalidField of invalidFields) {
            if (invalidField.id === 'ccnumber') {
                ccNumberValidationErrorEl.innerText = invalidField.value;
                this.domSvc.showElementWithCss(ccNumberValidationErrorEl);
            } else if (invalidField.id === 'ccexp') {
                ccExpirationValidationErrorEl.innerText = invalidField.value;
                this.domSvc.showElementWithCss(ccExpirationValidationErrorEl);
            } else if (invalidField.id === 'cvv') {
                cvvValidationErrorEl.innerText = invalidField.value;
                this.domSvc.showElementWithCss(cvvValidationErrorEl);
            } else if (invalidField.id === 'first_name') {

            } else if (invalidField.id === 'last_name') {

            } else if (invalidField.id === 'email') {

            }
        }
    }

    paymentStarted(): void {
        this.flags.paymentStarted = true;
        this.disableButton(this.cancelBtnEl);
        this.disableButton(this.proceedBtnEl);
        // TODO: How can we disable Revere fields ?
    }

    setPaymentInProgressState(): void {
        // TODO: We could show some text in the dialog like "Please wait, your payment is processed..."
    }

    setPaymentErrorState(message: string): void {
        // TODO: We could show some text in the dialog like "Payment failed, try again later"
        //       We should also restore the states of Cancel and Proceed buttons
    }

    private disableButton(button: HTMLButtonElement): void {
        button.disabled = true;
        this.domSvc.addClass(button, ClassName.azaDisabledButton);
    }

    private enableButton(button: HTMLButtonElement): void {
        button.disabled = null;
        this.domSvc.removeClass(button, ClassName.azaDisabledButton);
    }

    private getDialogResult(proceed: boolean): IRevereCheckoutDialogResult {
        const result: IRevereCheckoutDialogResult = {
            email: (this.modalDialogEl.querySelector(this.selectors.azaRwsRevereCheckoutEmailField) as HTMLInputElement).value,
            firstName: (this.modalDialogEl.querySelector(this.selectors.azaRwsRevereCheckoutFirstNameField) as HTMLInputElement).value,
            lastName: (this.modalDialogEl.querySelector(this.selectors.azaRwsRevereCheckoutLastNameField) as HTMLInputElement).value,
            proceed,

        };
        return result;
    }

    // private unsubscribe(): void {
    //     this.subscriptions.forEach(x => x.unsubscribe());
    //     this.subscriptions = [];
    // }
}

interface IFlags {
    paymentStarted?: boolean;
}
